/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module  public/js/core/firestoreservice.js
 * @class   FirestoreService
 * @summary Thin Firestore wrapper adding naive cache + batched‑write helpers.
 *
 * @description
 * Standardises Firestore access across TopSail’s front‑end by wrapping the v9
 * modular SDK calls.  Adds opt‑in in‑memory caching, batched commits and
 * consistent error handling so controllers/stores don’t duplicate boilerplate.
 *
 * ## Key Features
 * – Lazy initialisation via `init(firebaseApp)` to avoid circular imports.
 * – Simple Map‑based cache with manual invalidation.
 * – Promisified CRUD helpers plus `runBatch()` and `query()`.
 * – Zero external dependencies – only Firebase v9 web SDK.
 *
 * ## Public API
 * – `init(firebaseApp)` — attach FirebaseApp & create Firestore instance.
 * – `getDocument(path, useCache?)` — read doc with optional cache hit.
 * – `setDocument(path, data, {merge?})` — create / overwrite doc.
 * – `updateDocument(path, partial)` — patch selected fields.
 * – `deleteDocument(path)` — remove doc & evict from cache.
 * – `runBatch(mutator)` — commit multiple writes atomically.
 * – `query(collection, constraints?, cacheDocs?)` — list docs.
 * – `clearCache()` — wipe in‑memory cache (eg. on logout).
 *
 * ## Usage
 *   import fsService from '/js/bootstrap/app.js';
 *   const profile = await fsService.getDocument(`users/${uid}/profile`);
 *
 * @author   TopSail AI
 * @version  1.0.0
 * @since    2025‑05‑11
 * ─────────────────────────────────────────────────────────────────────────────
 */

// Modular SDK imports — done once; tree‑shaken by Vite / esbuild
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  query as buildQuery,
  getDocs,
  writeBatch,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { onAuthStateChanged } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-auth.js';
import { auth } from './firebase-service.js';
import useTenant from './useTenant.js';
import {
  docWithTenant,
  colWithTenant,
  writeWithTenant,
} from '../utils/firestoreHelpers.js';

const tenantStore = useTenant();

export default class FirestoreService {
  /** @type {import("firebase/firestore").Firestore|null} */
  #db = null;

  /** @type {Map<string, unknown>} */
  #cache = new Map();

  /** @type {Map<string, boolean|Promise<boolean>>} */
  #tenantOverrideCache = new Map();

  constructor(firestoreDb) {
    if (firestoreDb) this.#db = firestoreDb;
  }

  /* ───────── initialiser ───────── */
  /**
   * Attach a Firebase App instance; safe to call multiple times.
   * @param {import('firebase/app').FirebaseApp} firebaseApp
   */
  init(firebaseApp) {
    if (this.#db) return; // already initialised
    this.#db = getFirestore(firebaseApp);
  }

  /* ───────── helpers ───────── */
  #assertDb() {
    if (!this.#db)
      throw new Error(
        'FirestoreService not initialised – call init(app) first'
      );
  }

  async #docRef(path) {
    const tenantId = await this.#getTenantId();
    return docWithTenant(tenantId, path);
  }

  /**
   * Determine whether the signed-in user may override their auth tenant with
   * the tenant explicitly selected in browser storage.
   *
   * Only global `super_admin` viewers should be allowed to browse a different
   * tenant than the one attached to their auth claim. Regular seller/admin
   * sessions must stay pinned to the authenticated tenant to avoid cross-tenant
   * reads/writes when stale storage survives a login transition.
   *
   * @param {import('firebase/auth').User|null|undefined} user
   * @returns {Promise<boolean>} True when the selected tenant may override auth.
   */
  async #canOverrideTenantFromStore(user) {
    const uid = String(user?.uid || '').trim();
    if (!uid) return false;

    const cached = this.#tenantOverrideCache.get(uid);
    if (typeof cached === 'boolean') return cached;
    if (cached) return cached;

    const loadPromise = (async () => {
      try {
        const claims =
          typeof user?.getIdTokenResult === 'function'
            ? (await user.getIdTokenResult())?.claims || {}
            : user?.token || {};
        return (
          claims?.roles?.super_admin === true || claims?.role === 'super_admin'
        );
      } catch (err) {
        console.warn('[FirestoreService] failed to inspect tenant override', {
          uid,
          message: err?.message || String(err),
        });
        return false;
      }
    })();

    this.#tenantOverrideCache.set(uid, loadPromise);
    const canOverride = await loadPromise;
    this.#tenantOverrideCache.set(uid, canOverride);
    return canOverride;
  }

  /**
   * Keep the browser tenant selector aligned with the authenticated tenant for
   * non-super-admin sessions so subsequent helpers resolve a consistent scope.
   *
   * @param {string|null} tenantId
   * @returns {void}
   */
  #syncTenantStore(tenantId) {
    if (!tenantId) return;
    if (tenantStore.getTenantId?.() === tenantId) return;
    tenantStore.setTenantId?.(tenantId);
  }

  async #getTenantId() {
    const active = tenantStore.getTenantId();
    if (auth.currentUser) {
      const canOverride = active
        ? await this.#canOverrideTenantFromStore(auth.currentUser)
        : false;
      if (active && canOverride) return active;

      const id = auth.currentUser.tenantId;
      if (!id) {
        if (active) return active;
        console.error('[FirestoreService] missing tenant ID');
        throw new Error('Missing tenant ID');
      }
      this.#syncTenantStore(id);
      return id;
    }
    return new Promise((resolve, reject) => {
      const unsub = onAuthStateChanged(auth, async (u) => {
        if (u) {
          unsub();
          const selectedTenant = tenantStore.getTenantId();
          const canOverride = selectedTenant
            ? await this.#canOverrideTenantFromStore(u)
            : false;
          if (selectedTenant && canOverride) {
            resolve(selectedTenant);
            return;
          }
          if (!u.tenantId) {
            if (selectedTenant) {
              resolve(selectedTenant);
              return;
            }
            console.error('[FirestoreService] missing tenant ID');
            reject(new Error('Missing tenant ID'));
          } else {
            this.#syncTenantStore(u.tenantId);
            resolve(u.tenantId);
          }
        }
      });
    });
  }

  /**
   * Public helper to resolve a user's tenantId.
   * Falls back to the current user when `uid` is omitted or matches.
   *
   * @param {string=} uid
   * @returns {Promise<string|null>}
   */
  async getTenantId(uid) {
    if (!uid || uid === auth.currentUser?.uid) {
      return this.#getTenantId();
    }
    try {
      this.#assertDb();
      const ref = doc(this.#db, 'Users', uid);
      const snap = await getDoc(ref);
      return snap.exists() ? (snap.data().tenantId ?? null) : null;
    } catch (err) {
      console.warn('[FirestoreService.getTenantId]', err);
      return null;
    }
  }

  /* ───────── CRUD ───────── */
  async getDocument(path, useCache = true) {
    this.#assertDb();
    const tenantId = await this.#getTenantId();
    const key = `${tenantId}:${path}`;
    const debugEnabled = globalThis?.__FM_DEBUG === true;
    if (useCache && this.#cache.has(key)) {
      if (debugEnabled)
        console.debug('[FirestoreService.trace]', path, 'memory');
      return this.#cache.get(key);
    }
    const snap = await getDoc(docWithTenant(tenantId, path));
    const src = snap.metadata.fromCache ? 'cache' : 'network';
    if (debugEnabled) console.debug('[FirestoreService.trace]', path, src);
    const data = snap.exists() ? snap.data() : null;
    if (useCache) this.#cache.set(key, data);
    return data;
  }

  async setDocument(path, data, { merge = false } = {}) {
    this.#assertDb();
    const tenantId = await this.#getTenantId();
    const ref = docWithTenant(tenantId, path);
    await writeWithTenant(tenantId, ref, data, { merge });
    // optimistic cache update
    const key = `${tenantId}:${path}`;
    this.#cache.set(
      key,
      merge ? { ...(this.#cache.get(key) || {}), ...data } : data
    );
  }

  async updateDocument(path, partial) {
    this.#assertDb();
    const tenantId = await this.#getTenantId();
    const key = `${tenantId}:${path}`;
    await updateDoc(docWithTenant(tenantId, path), partial);
    this.#cache.set(key, { ...(this.#cache.get(key) || {}), ...partial });
  }

  async deleteDocument(path) {
    this.#assertDb();
    const tenantId = await this.#getTenantId();
    await deleteDoc(docWithTenant(tenantId, path));
    this.#cache.delete(`${tenantId}:${path}`);
  }

  /* ───────── batch/write & query ───────── */
  /**
   * Mutator receives a WriteBatch; call `batch.set/update/delete` as needed.
   * The commit happens automatically at the end.
   * @param {(batch: import('firebase/firestore').WriteBatch)=>void} mutator
   */
  async runBatch(mutator) {
    this.#assertDb();
    const batch = writeBatch(this.#db);
    mutator(batch);
    await batch.commit();
  }

  /**
   * Simple collection query wrapper.
   * @param {string} collectionPath e.g. "users"
   * @param {import('firebase/firestore').QueryConstraint[]} [constraints=[]]
   * @param {boolean} [cacheDocs=false]
   */
  async query(collectionPath, constraints = [], cacheDocs = false) {
    this.#assertDb();
    const tenantId = await this.#getTenantId();
    const base = colWithTenant(tenantId, collectionPath);
    const q = constraints.length ? buildQuery(base, ...constraints) : base;

    const snap = await getDocs(q);
    const rows = [];
    snap.forEach((d) => {
      const obj = { id: d.id, ...d.data() };
      rows.push(obj);
      if (cacheDocs)
        this.#cache.set(`${tenantId}:${collectionPath}/${d.id}`, obj);
    });
    return rows;
  }

  /* ───────── cache control ───────── */
  clearCache() {
    this.#cache.clear();
  }
}
